'use client';

import React, { useCallback, useEffect, useRef, useState } from 'react';

import { Box, Button, Paper, Popper, useMediaQuery } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { icons } from '@peerless/ui-assets/icons';
import { Animation } from './Animation';
import { QuickSearchWidget } from '../../components/QuickSearchWidget';
import { QuickSearchContainer } from './QuickSearchContainer';
import { theme } from '../../theme';

export const HeaderNavBarQuickSearch: React.FC = () => {
  const anchorEl = useRef<HTMLDivElement>(null);
  const quickSearchRef = useRef<{ reset: () => void }>(null);
  const [showQuickSearch, setShowQuickSearch] = useState(false);

  const handleClose = useCallback(() => {
    if (quickSearchRef.current) {
      quickSearchRef.current.reset();
    }
    setShowQuickSearch(false);
  }, []);

  useEffect(() => {
    if (!showQuickSearch) {
      handleClose();
    }
  }, [showQuickSearch, handleClose]);

  // Use media queries to determine the current screen size
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
  const isMediumScreen = useMediaQuery(theme.breakpoints.between('md', 'xl'));

  // Conditionally set the offset based on breakpoints
  const getOffset = () => {
    if (isSmallScreen) return [0, 8]; 
    if (isMediumScreen) return [0, 24]; 
    return [0, 24]; // Default offset
  };

  return (
    <QuickSearchContainer
      ref={quickSearchRef}
      onClose={handleClose}
      render={onClose => (
        <Box sx={{ position: 'relative' }} ref={anchorEl}>
          <Button
            variant="outlined"
            color="secondary"
            onClick={evt => {
              evt.preventDefault();
              setShowQuickSearch(value => !value);
            }}
          >
            <FontAwesomeIcon icon={icons.magnify} />
          </Button>
          <Popper
            anchorEl={anchorEl.current}
            open={showQuickSearch}
            placement="bottom-end"
            transition
            modifiers={[
              {
                name: 'offset',
                options: {
                  offset: getOffset(), // Dynamically set offset based on screen size
                },
              },
            ]}
            keepMounted
            sx={{ width: { xs: '100%', md: '40rem' }, zIndex: 2 }}
          >
            {({ TransitionProps }) => (
              // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- TransitionProps is always defined based on transition prop above
              <Animation {...TransitionProps!}>
                <Paper elevation={3} square sx={{ padding: theme => theme.spacing(3, 3) }}>
                  <QuickSearchWidget onClose={onClose} />
                </Paper>
              </Animation>
            )}
          </Popper>
        </Box>
      )}
    />
  );
};
